import { useEffect, useRef } from 'react';

import { DETAILS } from '@web-platform/shared-banana-constants/src/routes';
import { userRequestedSearch } from '@web-platform/shared-banana-redux/src/actions/searchControls';
import { matchHashPath } from '@web-platform/shared-banana-utils-frontend/src/helpers/removeHashSymbol';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { locationChanged } from '@web-platform/shared-universal-redux/src/actions/location';
import { searchParamsChanged } from '@web-platform/shared-universal-redux/src/actions/user';
import { getUserSearch } from '@web-platform/shared-universal-redux/src/state';
import { trackSmartMetric } from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { isAcornRequest } from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { SearchParams } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { Nullable } from '@web-platform/shared-universal-types-utils/src/utils';
import type { Location } from 'react-router-dom';

const isDetailsPanelLocation = (hash: string) => {
  if (hash.length === 0) {
    return null;
  }

  const match = matchHashPath(hash, DETAILS);

  return match && match.params && match.params.itineraryId ? match.params.itineraryId : null;
};

const isBananaPathBookingPanelLocation = (pathname: string) =>
  /\/(?:config|itinerary)\/[^/]+\/?$/.test(pathname);

// Key and state will change if we execute `replaceState` without actually changing the location (url).
// We ignore them here because we only care about the url changes
const omitState = ({ key, state, ...rest }: Location) => rest;

const getSearchParamsFromLocationState = (loc: Location): Nullable<SearchParams> => {
  if (loc.state != null && Object.prototype.hasOwnProperty.call(loc.state, 'searchParams')) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return loc.state.searchParams as SearchParams;
  }

  return null;
};

export const useSyncReduxStateWithUrlLocation = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // redux state
  const userSearch = useSelector(getUserSearch);

  const prevLocation = useRef(location);
  const hasProcessedLocation = useRef(false);

  useEffect(() => {
    if (!isEqual(omitState(location), omitState(prevLocation.current))) {
      const isPathBasedBookingPanel =
        !isAcornRequest() && getFeatureDecision(FEATURE_DECISION_KEYS.BOOKING_PANEL_WITHOUT_MODAL);
      const prevLocationIsPanel = isPathBasedBookingPanel
        ? isBananaPathBookingPanelLocation(prevLocation.current.pathname)
        : isDetailsPanelLocation(prevLocation.current.hash);
      const currLocationIsPanel = isPathBasedBookingPanel
        ? isBananaPathBookingPanelLocation(location.pathname)
        : isDetailsPanelLocation(location.hash);

      if (prevLocationIsPanel && !currLocationIsPanel) {
        trackInteractionEvent({
          name: ANALYTICS_EVENTS.INTERACTION.FLIGHT_BOOKING_PANEL_CLOSED.NAME,
        });
        trackSmartMetric('Booking_Panel_Closed');
      }

      dispatch(locationChanged(location, prevLocation.current));
      prevLocation.current = location;

      // Reset search params dispatch flag for new location
      hasProcessedLocation.current = false;
    }

    // URL in browser
    const updatedSearchParams = getSearchParamsFromLocationState(location);
    if (
      updatedSearchParams != null &&
      !isEqual(updatedSearchParams, userSearch) &&
      !isAcornRequest() &&
      !hasProcessedLocation.current
    ) {
      dispatch(searchParamsChanged(updatedSearchParams));
      dispatch(userRequestedSearch(true));
      hasProcessedLocation.current = true;
    }

    // We only want to sync location -> userSearch (not vice-versa)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location, prevLocation]);
};
